import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import theme from './theme';
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import Home from './pages/Home';
import About from './pages/About';
import Nominations from './pages/Nominations';
import Winners from './pages/Winners';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsAndConditions from './pages/TermsAndConditions';
import ComingSoon from './pages/ComingSoon';
import Sponsor from './pages/Sponsor';

// Language wrapper component
const LanguageWrapper = ({ children }) => {
  const { lang } = useParams();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (lang && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  return children;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <ScrollToTop />
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
          <Header />
          <Box component="main" sx={{ flexGrow: 1 }}>
            <Routes>
              {/* Redirect root to default language (English) */}
              <Route path="/" element={<Navigate to="/en" replace />} />

              {/* Language-specific routes */}
              <Route path="/:lang" element={
                <LanguageWrapper>
                  <Home />
                </LanguageWrapper>
              } />
              <Route path="/:lang/about" element={
                <LanguageWrapper>
                  <About />
                </LanguageWrapper>
              } />
              <Route path="/:lang/nominations" element={
                <LanguageWrapper>
                  <Nominations />
                </LanguageWrapper>
              } />
              <Route path="/:lang/winners" element={
                <LanguageWrapper>
                  <Winners />
                </LanguageWrapper>
              } />
              <Route path="/:lang/sponsor" element={
                <LanguageWrapper>
                  <Sponsor />
                </LanguageWrapper>
              } />
              <Route path="/:lang/coming-soon" element={
                <LanguageWrapper>
                  <ComingSoon />
                </LanguageWrapper>
              } />
              <Route path="/:lang/privacy-policy" element={
                <LanguageWrapper>
                  <PrivacyPolicy />
                </LanguageWrapper>
              } />
              <Route path="/:lang/terms-and-conditions" element={
                <LanguageWrapper>
                  <TermsAndConditions />
                </LanguageWrapper>
              } />

              {/* Legacy routes - redirect to English */}
              <Route path="/about" element={<Navigate to="/en/about" replace />} />
              <Route path="/nominations" element={<Navigate to="/en/nominations" replace />} />
              <Route path="/winners" element={<Navigate to="/en/winners" replace />} />
              <Route path="/sponsor" element={<Navigate to="/en/sponsor" replace />} />
              <Route path="/coming-soon" element={<Navigate to="/en/coming-soon" replace />} />
              <Route path="/privacy-policy" element={<Navigate to="/en/privacy-policy" replace />} />
              <Route path="/terms-and-conditions" element={<Navigate to="/en/terms-and-conditions" replace />} />

              {/* Catch all */}
              <Route path="*" element={<Navigate to="/en" replace />} />
            </Routes>
          </Box>
          <Footer />
        </Box>
      </Router>
    </ThemeProvider>
  );
}

export default App;
